/**
 * Iter64 — Rastreo actual: estado consolidado de las 4 fuentes y avisos pendientes.
 */
import React, { useEffect, useState } from 'react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Activity, RefreshCw, Bell, Sparkles, Globe2, Database } from 'lucide-react';
import { toast } from 'sonner';
import { req } from '../services/api';

const ICONS = {
  'Hacienda ZIP': Database,
  'APIspaín': Globe2,
  'Catalunya': Globe2,
  'Tavily+Gemini': Sparkles,
};

const ESTADO_COLOR = {
  ok: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  completado: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  procesando: 'bg-amber-50 text-amber-700 border-amber-200',
  descargando: 'bg-blue-50 text-blue-700 border-blue-200',
  error: 'bg-red-50 text-red-700 border-red-200',
  'sin ejecutar': 'bg-ink-50 text-ink-500 border-ink-200',
};

export default function RastreoActual() {
  const [data, setData] = useState(null);
  const [running, setRunning] = useState(null);  // nombre del que está corriendo

  const cargar = async () => {
    try { setData(await req('/api/prospeccion/fuentes/estado')); }
    catch (e) { toast.error(e.message); }
  };

  useEffect(() => { cargar(); const t = setInterval(cargar, 6000); return () => clearInterval(t); }, []);

  const ejecutar = async (fuente) => {
    setRunning(fuente);
    try {
      let path = null;
      if (fuente === 'Hacienda ZIP') path = '/api/scheduler/run/hacienda_mensual_agua';
      else if (fuente === 'APIspaín') path = '/api/prospeccion/apispain/run';
      else if (fuente === 'Tavily+Gemini') path = '/api/scheduler/run/enriquecer_gestoras_municipios';
      else { toast.info('Ejecución manual no soportada para esta fuente'); setRunning(null); return; }
      await req(path, { method: 'POST', body: JSON.stringify({}) });
      toast.success(`${fuente} ejecutado`);
      cargar();
    } catch (e) {
      toast.error(`${fuente}: ${e.message}`);
    } finally { setRunning(null); }
  };

  return (
    <div className="space-y-6" data-testid="page-rastreo-actual">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-heading font-bold text-3xl text-ink-900 flex items-center gap-2">
            <Activity className="w-7 h-7 text-brand-500" /> Rastreo actual
          </h1>
          <p className="text-sm text-ink-500 mt-1">
            Estado consolidado de las 4 fuentes de prospección. Ejecuta manualmente o deja
            que el scheduler las dispare en su cadencia.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={cargar}>
          <RefreshCw className="w-4 h-4 mr-1.5" /> Actualizar
        </Button>
      </div>

      <Card className="p-5 border-ink-200 bg-white" data-testid="rastreo-avisos">
        <div className="flex items-center gap-3">
          <Bell className={`w-5 h-5 ${data?.avisos_pendientes ? 'text-amber-500' : 'text-ink-300'}`} />
          <div>
            <p className="font-heading font-semibold text-ink-800">
              {data?.avisos_pendientes || 0} aviso{data?.avisos_pendientes === 1 ? '' : 's'} pendiente{data?.avisos_pendientes === 1 ? '' : 's'}
            </p>
            <p className="text-xs text-ink-500">Licitaciones nuevas de agua encontradas en el último ciclo</p>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="rastreo-fuentes">
        {(data?.fuentes || []).map((f) => {
          const Icon = ICONS[f.nombre] || Activity;
          const estadoClass = ESTADO_COLOR[f.estado] || ESTADO_COLOR['sin ejecutar'];
          return (
            <Card key={f.nombre} className="p-5 border-ink-200 bg-white" data-testid={`rastreo-fuente-${f.nombre}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Icon className="w-5 h-5 text-brand-500" />
                  <h3 className="font-heading font-semibold text-ink-900">{f.nombre}</h3>
                </div>
                <Badge className={`${estadoClass} border`}>{f.estado}</Badge>
              </div>
              <div className="text-sm text-ink-600 space-y-1">
                <p>Último run: <span className="font-mono text-xs">{f.ultimo_run?.slice(0, 19).replace('T', ' ') || 'Nunca'}</span></p>
                <p>Nuevos en último ciclo: <strong>{(f.nuevos || 0).toLocaleString('es-ES')}</strong></p>
              </div>
              <div className="mt-3">
                <Button
                  size="sm" variant="outline"
                  onClick={() => ejecutar(f.nombre)}
                  disabled={running === f.nombre}
                  data-testid={`btn-ejecutar-${f.nombre}`}
                >
                  {running === f.nombre ? 'Ejecutando…' : 'Ejecutar ahora'}
                </Button>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
